import React, { useState, useMemo } from 'react';
import {
  FlaskConical, TrendingUp, TrendingDown, CheckCircle,
  Clock, Play, Pause, BarChart3, Target, Bot, Lightbulb,
  ArrowRight, Eye, Zap
} from 'lucide-react';
import { tone, toneForStatus } from "components/ui/tone";

// A/B Test Status Badge Component
export const ABTestStatusBadge = ({ test, size = 'medium' }) => {
  const getStatusConfig = (status) => {
    // Icon and label are content; the tint comes from the shared mapping.
    const CONFIG = {
      running: { color: tone(toneForStatus('running')).badge, icon: Play, label: 'Running' },
      completed: { color: tone(toneForStatus('completed')).badge, icon: CheckCircle, label: 'Completed' },
      paused: { color: tone(toneForStatus('paused')).badge, icon: Pause, label: 'Paused' },
      draft: { color: tone(toneForStatus('draft')).badge, icon: Clock, label: 'Draft' },
      awaiting_review: { color: tone(toneForStatus('awaiting_review')).badge, icon: Eye, label: 'Review' },
    };
    return CONFIG[status] ?? CONFIG['running'];
  };

  const config = getStatusConfig(test.status);
  const Icon = config.icon;
  const sizeClasses = size === 'small' ? 'text-xs px-2 py-1' : 'text-sm px-3 py-1';

  return (
    <div className={`inline-flex items-center gap-1 rounded-full border ${config.color} ${sizeClasses}`}>
      <Icon className={`${size === 'small' ? 'w-3 h-3' : 'w-4 h-4'}`} />
      <span className="font-medium">{config.label}</span>
    </div>
  );
};

// A/B Test Results Summary Component
export const ABTestResultsSummary = ({ test, compact = false }) => {
  const { metrics } = test;
  
  if (!metrics?.primaryMetric) {
    return (
      <div className="text-sm text-content-muted">
        No results available yet
      </div>
    );
  }

  const { primaryMetric } = metrics;
  const improvement = primaryMetric.improvement;
  const isSignificant = primaryMetric.significance >= 0.95;
  const hasWinner = isSignificant && Math.abs(improvement) > 5;

  const formatPercentage = (value) => `${value.toFixed(1)}%`;

  if (compact) {
    return (
      <div className="flex items-center gap-2 text-sm">
        <div className={`flex items-center gap-1 ${
          improvement > 0 ? 'text-green-600 dark:text-green-300' : improvement < 0 ? 'text-red-600 dark:text-red-300' : 'text-content-secondary'
        }`}>
          {improvement > 0 ? (
            <TrendingUp className="w-4 h-4" />
          ) : improvement < 0 ? (
            <TrendingDown className="w-4 h-4" />
          ) : (
            <BarChart3 className="w-4 h-4" />
          )}
          <span className="font-medium">
            {improvement > 0 ? '+' : ''}{formatPercentage(improvement)}
          </span>
        </div>
        {isSignificant && (
          <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-2 py-1 rounded-full">
            Significant
          </span>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Winner Declaration */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FlaskConical className="w-5 h-5 text-blue-600 dark:text-blue-300" />
          <span className="font-medium text-content">{primaryMetric.name}</span>
        </div>
        <div className={`px-3 py-1 rounded-full text-sm font-medium ${
          hasWinner
            ? improvement > 0
              ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
              : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
            : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300'
        }`}>
          {hasWinner
            ? improvement > 0
              ? '🏆 Variant Wins'
              : '🛡️ Control Wins'
            : '⚖️ Inconclusive'
          }
        </div>
      </div>

      {/* Performance Comparison */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-gray-50 dark:bg-dark-700 rounded-lg p-3">
          <div className="text-sm text-content-secondary mb-1">Control</div>
          <div className="font-semibold text-content">
            {formatPercentage(primaryMetric.control.value)}
          </div>
          <div className="text-xs text-content-muted">
            {primaryMetric.control.sampleSize.toLocaleString()} samples
          </div>
        </div>
        
        <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-3">
          <div className="text-sm text-content-secondary mb-1">Variant</div>
          <div className="font-semibold text-content">
            {formatPercentage(primaryMetric.variants[0].value)}
          </div>
          <div className="text-xs text-content-muted">
            {primaryMetric.variants[0].sampleSize.toLocaleString()} samples
          </div>
        </div>
      </div>

      {/* Statistical Summary */}
      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center gap-2">
          <span className="text-content-secondary">Improvement:</span>
          <span className={`font-medium ${
            improvement > 0 ? 'text-green-600 dark:text-green-300' : improvement < 0 ? 'text-red-600 dark:text-red-300' : 'text-content-secondary'
          }`}>
            {improvement > 0 ? '+' : ''}{formatPercentage(improvement)}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-content-secondary">Confidence:</span>
          <span className={`font-medium ${isSignificant ? 'text-green-600 dark:text-green-300' : 'text-yellow-600 dark:text-yellow-300'}`}>
            {Math.round(primaryMetric.significance * 100)}%
          </span>
        </div>
      </div>
    </div>
  );
};

// AI Agent Collaboration Display Component
export const AIAgentCollaboration = ({ campaign, aiAgents = [], abTests = [] }) => {
  const [showDetails, setShowDetails] = useState(false);

  // Find AI agents involved in this campaign
  const involvedAgents = useMemo(() => {
    const agents = [];
    
    // Campaign creation agent
    if (campaign.aiGenerated && campaign.aiAgentId) {
      const campaignAgent = aiAgents.find(agent => agent.id === campaign.aiAgentId);
      if (campaignAgent) {
        agents.push({
          ...campaignAgent,
          role: 'Campaign Creator',
          contribution: 'Generated campaign content and strategy'
        });
      }
    }

    // A/B testing agents
    const campaignTests = abTests.filter(test => test.templateId === campaign.templateId);
    campaignTests.forEach(test => {
      if (test.aiAgentId) {
        const testAgent = aiAgents.find(agent => agent.id === test.aiAgentId);
        if (testAgent && !agents.find(a => a.id === testAgent.id)) {
          agents.push({
            ...testAgent,
            role: 'A/B Test Optimizer',
            contribution: 'Managing test variants and optimization'
          });
        }
      }
    });

    return agents;
  }, [campaign, aiAgents, abTests]);

  if (involvedAgents.length === 0) {
    return null;
  }

  return (
    <div className="border border-purple-200 dark:border-purple-800/50 rounded-lg p-4 bg-purple-50 dark:bg-purple-900/30">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Bot className="w-5 h-5 text-purple-600 dark:text-purple-300" />
          <span className="font-medium text-purple-900 dark:text-purple-300">AI Agent Collaboration</span>
          <span className="text-sm bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 px-2 py-1 rounded-full">
            {involvedAgents.length} agent{involvedAgents.length !== 1 ? 's' : ''}
          </span>
        </div>
        <button
          onClick={() => setShowDetails(!showDetails)}
          className="text-sm text-purple-600 dark:text-purple-300 hover:text-purple-700 dark:hover:text-purple-300 font-medium"
        >
          {showDetails ? 'Hide Details' : 'Show Details'}
        </button>
      </div>

      <div className="flex items-center gap-2 mb-2">
        {involvedAgents.map((agent, index) => (
          <div key={agent.id} className="flex items-center gap-1">
            {index > 0 && <ArrowRight className="w-3 h-3 text-purple-400" />}
            <div className="flex items-center gap-1 bg-surface rounded-full px-2 py-1 border border-purple-200 dark:border-purple-800/50">
              <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
              <span className="text-xs font-medium text-purple-900 dark:text-purple-300">{agent.name}</span>
            </div>
          </div>
        ))}
      </div>

      {showDetails && (
        <div className="space-y-3 mt-3 pt-3 border-t border-purple-200 dark:border-purple-800/50">
          {involvedAgents.map(agent => (
            <div key={agent.id} className="bg-surface rounded-lg p-3 border border-purple-100 dark:border-purple-800/50">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                  <span className="font-medium text-content">{agent.name}</span>
                  <span className="text-sm text-purple-600 dark:text-purple-300 bg-purple-100 dark:bg-purple-900/30 px-2 py-1 rounded">
                    {agent.role}
                  </span>
                </div>
                <div className="text-sm text-content-secondary">
                  Score: {agent.performance?.score || 85}/100
                </div>
              </div>
              <p className="text-sm text-content-secondary mb-2">{agent.contribution}</p>
              <div className="flex items-center gap-4 text-xs text-content-muted">
                <span>Specialty: {agent.specialty}</span>
                <span>•</span>
                <span>Confidence: {agent.performance?.confidenceLevel || 92}%</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Campaign Automation Workflow Indicator
export const CampaignAutomationWorkflow = ({ campaign, automationSequences = [] }) => {
  const [showWorkflow, setShowWorkflow] = useState(false);

  // Find automation sequences for this campaign
  const campaignSequences = automationSequences.filter(seq => 
    seq.campaignId === campaign.id || seq.triggerCampaignId === campaign.id
  );

  if (campaignSequences.length === 0 && !campaign.automationTriggers?.length) {
    return null;
  }

  const hasActiveAutomation = campaignSequences.some(seq => seq.status === 'active') ||
    campaign.automationTriggers?.some(trigger => trigger.isActive);

  return (
    <div className="border border-blue-200 dark:border-blue-800/50 rounded-lg p-4 bg-blue-50 dark:bg-blue-900/30">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Zap className="w-5 h-5 text-blue-600 dark:text-blue-300" />
          <span className="font-medium text-blue-900 dark:text-blue-300">Automation Workflow</span>
          {hasActiveAutomation && (
            <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-2 py-1 rounded-full">
              Active
            </span>
          )}
        </div>
        <button
          onClick={() => setShowWorkflow(!showWorkflow)}
          className="text-sm text-blue-600 dark:text-blue-300 hover:text-blue-700 dark:hover:text-blue-300 font-medium"
        >
          {showWorkflow ? 'Hide Workflow' : 'Show Workflow'}
        </button>
      </div>

      <div className="text-sm text-blue-800 dark:text-blue-300 mb-2">
        {campaignSequences.length > 0 
          ? `Part of ${campaignSequences.length} automation sequence${campaignSequences.length !== 1 ? 's' : ''}`
          : 'Standalone automated campaign'
        }
      </div>

      {showWorkflow && (
        <div className="space-y-3 mt-3 pt-3 border-t border-blue-200 dark:border-blue-800/50">
          {/* Automation Triggers */}
          {campaign.automationTriggers?.length > 0 && (
            <div className="bg-surface rounded-lg p-3 border border-blue-100 dark:border-blue-800/50">
              <h4 className="font-medium text-content mb-2">Triggers</h4>
              <div className="space-y-2">
                {campaign.automationTriggers.map((trigger, index) => (
                  <div key={index} className="flex items-center gap-2 text-sm">
                    <Target className="w-4 h-4 text-blue-600 dark:text-blue-300" />
                    <span className="text-gray-700 dark:text-gray-300">{trigger.description}</span>
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      trigger.isActive 
                        ? 'bg-green-100 text-green-700 dark:text-green-300' 
                        : 'bg-gray-100 dark:bg-dark-700 text-content-secondary'
                    }`}>
                      {trigger.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Automation Sequences */}
          {campaignSequences.map(sequence => (
            <div key={sequence.id} className="bg-surface rounded-lg p-3 border border-blue-100 dark:border-blue-800/50">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-content">{sequence.name}</h4>
                <span className={`px-2 py-1 rounded-full text-xs ${
                  sequence.status === 'active'
                    ? 'bg-green-100 text-green-700 dark:text-green-300'
                    : sequence.status === 'paused'
                    ? 'bg-yellow-100 text-yellow-700 dark:text-yellow-300'
                    : 'bg-gray-100 dark:bg-dark-700 text-content-secondary'
                }`}>
                  {sequence.status}
                </span>
              </div>
              <p className="text-sm text-content-secondary mb-2">{sequence.description}</p>
              <div className="flex items-center gap-4 text-xs text-content-muted">
                <span>Steps: {sequence.steps?.length || 0}</span>
                <span>•</span>
                <span>Completion Rate: {sequence.completionRate || 0}%</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// A/B Test Quick Setup Component
export const ABTestQuickSetup = ({ campaign, onCreateTest, onViewTests }) => {
  const [showSetup, setShowSetup] = useState(false);

  const handleQuickTest = (testType) => {
    const testConfig = {
      campaignId: campaign.id,
      templateId: campaign.templateId,
      type: testType,
      name: `${campaign.name} - ${testType} Test`,
      quickSetup: true
    };
    
    onCreateTest(testConfig);
    setShowSetup(false);
  };

  return (
    <div className="border border-orange-200 dark:border-orange-800/50 rounded-lg p-4 bg-orange-50 dark:bg-orange-900/30">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Lightbulb className="w-5 h-5 text-orange-600 dark:text-orange-300" />
          <span className="font-medium text-orange-900 dark:text-orange-300">A/B Test Opportunities</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowSetup(!showSetup)}
            className="text-sm text-orange-600 dark:text-orange-300 hover:text-orange-700 dark:hover:text-orange-300 font-medium"
          >
            Quick Setup
          </button>
          <button
            onClick={onViewTests}
            className="text-sm text-orange-600 dark:text-orange-300 hover:text-orange-700 dark:hover:text-orange-300 font-medium"
          >
            View Tests
          </button>
        </div>
      </div>

      <div className="text-sm text-orange-800 dark:text-orange-300 mb-2">
        Optimize this campaign with automated A/B testing
      </div>

      {showSetup && (
        <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t border-orange-200 dark:border-orange-800/50">
          <button
            onClick={() => handleQuickTest('subject_line')}
            className="p-2 bg-surface border border-orange-200 dark:border-orange-800/50 rounded-lg hover:bg-orange-100 dark:hover:bg-orange-900/30 text-sm text-orange-900 dark:text-orange-300 font-medium"
          >
            Subject Line Test
          </button>
          <button
            onClick={() => handleQuickTest('cta_button')}
            className="p-2 bg-surface border border-orange-200 dark:border-orange-800/50 rounded-lg hover:bg-orange-100 dark:hover:bg-orange-900/30 text-sm text-orange-900 dark:text-orange-300 font-medium"
          >
            CTA Button Test
          </button>
          <button
            onClick={() => handleQuickTest('content')}
            className="p-2 bg-surface border border-orange-200 dark:border-orange-800/50 rounded-lg hover:bg-orange-100 dark:hover:bg-orange-900/30 text-sm text-orange-900 dark:text-orange-300 font-medium"
          >
            Content Test
          </button>
          <button
            onClick={() => handleQuickTest('send_time')}
            className="p-2 bg-surface border border-orange-200 dark:border-orange-800/50 rounded-lg hover:bg-orange-100 dark:hover:bg-orange-900/30 text-sm text-orange-900 dark:text-orange-300 font-medium"
          >
            Send Time Test
          </button>
        </div>
      )}
    </div>
  );
};

// Main A/B Testing Integration Component
export const ABTestingIntegration = ({ 
  campaign, 
  abTests = [], 
  aiAgents = [], 
  automationSequences = [],
  onCreateTest,
  onViewTestDetails,
  onViewAllTests
}) => {
  // Find A/B tests for this campaign
  const campaignTests = abTests.filter(test => 
    test.templateId === campaign.templateId || test.campaignId === campaign.id
  );

  const activeTests = campaignTests.filter(test => test.status === 'running');
  const completedTests = campaignTests.filter(test => test.status === 'completed');

  return (
    <div className="space-y-4">
      {/* Active A/B Tests */}
      {activeTests.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-content">Active A/B Tests</h4>
            <button
              onClick={onViewAllTests}
              className="text-sm text-blue-600 dark:text-blue-300 hover:text-blue-700 dark:hover:text-blue-300 font-medium"
            >
              View All Tests
            </button>
          </div>
          {activeTests.map(test => (
            <div key={test.id} className="border border-blue-200 dark:border-blue-800/50 rounded-lg p-4 bg-blue-50 dark:bg-blue-900/30">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <FlaskConical className="w-4 h-4 text-blue-600 dark:text-blue-300" />
                  <span className="font-medium text-blue-900 dark:text-blue-300">{test.name}</span>
                  <ABTestStatusBadge test={test} size="small" />
                </div>
                <button
                  onClick={() => onViewTestDetails(test)}
                  className="text-sm text-blue-600 dark:text-blue-300 hover:text-blue-700 dark:hover:text-blue-300 font-medium"
                >
                  View Details
                </button>
              </div>
              <ABTestResultsSummary test={test} compact={true} />
            </div>
          ))}
        </div>
      )}

      {/* Completed Tests Summary */}
      {completedTests.length > 0 && (
        <div className="space-y-3">
          <h4 className="font-medium text-content">Recent Test Results</h4>
          {completedTests.slice(0, 2).map(test => (
            <div key={test.id} className="border border-green-200 dark:border-green-800/50 rounded-lg p-4 bg-green-50 dark:bg-green-900/30">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-300" />
                  <span className="font-medium text-green-900 dark:text-green-300">{test.name}</span>
                  <ABTestStatusBadge test={test} size="small" />
                </div>
                <button
                  onClick={() => onViewTestDetails(test)}
                  className="text-sm text-green-600 dark:text-green-300 hover:text-green-700 dark:hover:text-green-300 font-medium"
                >
                  View Results
                </button>
              </div>
              <ABTestResultsSummary test={test} compact={true} />
            </div>
          ))}
        </div>
      )}

      {/* AI Agent Collaboration */}
      <AIAgentCollaboration 
        campaign={campaign}
        aiAgents={aiAgents}
        abTests={campaignTests}
      />

      {/* Automation Workflow */}
      <CampaignAutomationWorkflow 
        campaign={campaign}
        automationSequences={automationSequences}
      />

      {/* Quick A/B Test Setup */}
      {campaignTests.length === 0 && (
        <ABTestQuickSetup 
          campaign={campaign}
          onCreateTest={onCreateTest}
          onViewTests={onViewAllTests}
        />
      )}
    </div>
  );
};

export default ABTestingIntegration;